<template>
    <div
        class="EntityDetailsProducerEditRow"
        :class="{ 'EntityDetailsProducerEditRow--default': row.isDefault }"
    >
        <div class="EntityDetailsProducerEditRow__head">
            <div class="EntityDetailsProducerEditRow__headGroup">
                <span class="EntityDetailsProducerEditRow__eyebrow">PRODUCER</span>
                <!-- With nothing to choose between, the badge states the designation that the
                     radio would otherwise carry. -->
                <LabelBadge
                    v-if="!selectable && row.isDefault"
                    variant="gradient"
                    size="xs"
                    data-test-id="EntityDetailsProducerEditRow__defaultBadge"
                >
                    DEFAULT
                </LabelBadge>
            </div>
            <div class="EntityDetailsProducerEditRow__headGroup">
                <RadioInput
                    v-if="selectable"
                    class="EntityDetailsProducerEditRow__defaultRadio"
                    :name="DEFAULT_RADIO_GROUP"
                    :value="row.key"
                    :model-value="defaultKey"
                    :label="row.isDefault ? 'Default producer' : 'Set as default'"
                    :disabled="disabled"
                    data-test-id="EntityDetailsProducerEditRow__defaultRadio"
                    @change="$emit('set-default', row.key)"
                />
                <NewButton
                    v-if="showRemove"
                    variant="flat"
                    color="neutral"
                    size="sm"
                    data-test-id="EntityDetailsProducerEditRow__remove"
                    @click="$emit('remove', row.key)"
                >
                    <EidIcon
                        :icon-src="faTrash"
                        alt="remove"
                        class="EntityDetailsProducerEditRow__removeIcon"
                    />
                    Remove
                </NewButton>
            </div>
        </div>

        <div class="EntityDetailsProducerEditRow__grid">
            <TextInput
                :id="`${row.key}-producerFirm`"
                label="Producer Firm"
                required
                :value="row.producerFirm"
                :disabled="disabled"
                :error="fieldError('producerFirm')"
                placeholder="Enter producer firm"
                data-test-id="EntityDetailsProducerEditRow__producerFirm"
                @input="onFieldInput('producerFirm', $event)"
            />
            <TextInput
                :id="`${row.key}-contactName`"
                label="Contact Name"
                required
                :value="row.contactName"
                :disabled="disabled"
                :error="fieldError('contactName')"
                placeholder="Enter contact name"
                data-test-id="EntityDetailsProducerEditRow__contactName"
                @input="onFieldInput('contactName', $event)"
            />
            <TextInput
                :id="`${row.key}-email`"
                label="Email"
                required
                :value="row.email"
                :disabled="disabled"
                :error="fieldError('email')"
                placeholder="Enter email address"
                data-test-id="EntityDetailsProducerEditRow__email"
                @input="onFieldInput('email', $event)"
            />
            <PhoneInput
                :id="`${row.key}-phone`"
                label="Phone"
                required
                :value="row.phone"
                :disabled="disabled"
                :error="fieldError('phone')"
                data-test-id="EntityDetailsProducerEditRow__phone"
                @change="onPhoneChange"
                @validity-change="onPhoneValidityChange"
            />
        </div>

        <div class="EntityDetailsProducerEditRow__coverages">
            <span class="EntityDetailsProducerEditRow__coverageLabel">
                Coverage Types
                <span
                    v-if="coverageRequired"
                    class="EntityDetailsProducerEditRow__requiredIndicator"
                >*</span>
            </span>
            <NewDropdown
                class="EntityDetailsProducerEditRow__dropdown"
                :class="{
                    'EntityDetailsProducerEditRow__dropdown--error': Boolean(fieldError('coverageTypes')),
                }"
                multi-select
                label="Select coverage types"
                :options="coverageOptions"
                :selected="selectedCoverageOptions"
                :disabled="disabled"
                data-test-id="EntityDetailsProducerEditRow__coverageTypes"
                @input="onCoverageInput"
            />
            <div
                v-if="fieldError('coverageTypes')"
                class="EntityDetailsProducerEditRow__error"
                data-test-id="EntityDetailsProducerEditRow__coverageError"
            >
                <EidIcon :icon-src="faExclamationTriangle" />
                <span>{{ fieldError('coverageTypes') }}</span>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.EntityDetailsProducerEditRow {
    padding: 16px 0;

    & + & {
        border-top: 1px solid var(--eidSecureBlue-10);
    }

    // Matches the read-mode entry so the default producer reads the same in both modes.
    &--default {
        border-left: 3px solid var(--eidTrustGreen);
        padding-left: 13px;
    }

    &__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 12px;
    }

    &__headGroup {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
    }

    // The radio is one control in a row, not a full-width form field. Qualified with .RadioInput
    // so it outranks that component's own `width: 100%` rather than depending on source order --
    // neither style block is scoped.
    &__defaultRadio.RadioInput {
        width: auto;
        white-space: nowrap;

        // The label sits beside Remove rather than over an input, so it takes that button's
        // weight and colour instead of the heavier treatment a field label carries.
        .RadioInput__text {
            font-size: var(--control-size-sm-font-size);
            line-height: var(--control-size-sm-line-height);
            font-weight: 400;
            color: var(--eidTechGray);
        }
    }

    &__eyebrow {
        font-family: "Roboto", sans-serif;
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0.05em;
        color: var(--eidQualityBlack-75);
    }

    &__removeIcon {
        font-size: 11px;
        margin-right: 6px;
    }

    &__grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px 16px;

        // Grid children must be allowed to shrink, otherwise long values overflow.
        > * {
            min-width: 0;
        }

        @media (min-width: 1100px) {
            grid-template-columns: 1fr 1fr;
        }
    }

    &__coverages {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 14px;
    }

    &__coverageLabel {
        display: flex;
        align-items: center;
        gap: 3px;
        font-family: "Roboto", sans-serif;
        font-size: 12px;
        font-weight: 500;
        line-height: 20px;
        color: var(--eidSecureBlue);
    }

    &__requiredIndicator {
        color: var(--eidDangerRed);
        font-size: 13px;
        font-weight: 600;
        line-height: 1;
    }

    &__dropdown {
        width: 100%;

        &--error {
            .NewDropdown .q-field__control::before {
                border-color: var(--eidDangerRed);
            }
        }
    }

    &__error {
        display: flex;
        gap: 7px;
        color: var(--eidDangerRed);
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faTrash } from '@fortawesome/pro-light-svg-icons';
    import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';

    import NewDropdown from '@evidentid/dashboard-commons/components/Dropdown/NewDropdown.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import LabelBadge from '@evidentid/dashboard-commons/components/LabelBadge/LabelBadge.vue';

    import NewButton from '@/components/new-button/NewButton.vue';
    import PhoneInput from '@/modules/dynamic-form/components/style-inputs/phone-input/PhoneInput.vue';
    import RadioInput from '@/modules/dynamic-form/components/style-inputs/radio-input/RadioInput.vue';
    import TextInput from '@/modules/dynamic-form/components/style-inputs/text-input/TextInput.vue';
    import {
        ProducerRow,
        ProducerRowErrors,
        ProducerRowField,
    } from '@/modules/entity-details/utils/producer-forms/producerRows';

    export default defineComponent({
        name: 'EntityDetailsProducerEditRow',
        components: {
            EidIcon,
            LabelBadge,
            NewButton,
            NewDropdown,
            PhoneInput,
            RadioInput,
            TextInput,
        },
        props: {
            row: {
                type: Object as PropType<ProducerRow>,
                required: true,
            },
            coverageOptions: {
                type: Array as PropType<DropdownOption[]>,
                default: () => [],
            },
            errors: {
                type: Object as PropType<ProducerRowErrors>,
                default: () => ({}) as ProducerRowErrors,
            },
            // Errors stay hidden until the user attempts a save, so a freshly added row is not
            // red before it has been filled in.
            showErrors: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            // Whether Remove is offered at all. Removal is governed by permission alone, never by
            // `disabled`: a producer whose fields are locked — one the entity's requirements no
            // longer cover — is still removable, and taking that away would strand it.
            showRemove: {
                type: Boolean,
                default: true,
            },
            // Whether a coverage type has to be picked. False when the entity has none to offer,
            // which is also when `validateProducerRows` stops demanding one.
            coverageRequired: {
                type: Boolean,
                default: true,
            },
            // Whether the entity has more than one producer to choose between. With one there is
            // nothing to pick, so the badge states the designation instead of offering a radio.
            selectable: {
                type: Boolean,
                default: false,
            },
            // Key of the row currently designated default, empty while none is.
            defaultKey: {
                type: String,
                default: '',
            },
        },
        emits: [ 'field-change', 'phone-change', 'coverages-change', 'set-default', 'remove' ],
        data() {
            return {
                // Shared across every row so the browser treats them as one group and unselects
                // the old default for us.
                DEFAULT_RADIO_GROUP: 'producer-default',
                faTrash,
                faExclamationTriangle,
                // Hold the most recent halves of the phone input's paired events so either one can
                // emit a complete update.
                latestPhone: null as string | null,
                latestPhoneValid: null as boolean | null,
            };
        },
        computed: {
            selectedCoverageOptions(): DropdownOption[] {
                return this.coverageOptions.filter(
                    (option) => this.row.coverageTypes.includes(String(option.value)),
                );
            },
        },
        methods: {
            fieldError(field: ProducerRowField | 'coverageTypes'): string {
                return this.showErrors ? (this.errors[field] || '') : '';
            },
            onFieldInput(field: ProducerRowField, event: Event | string): void {
                const value = typeof event === 'string'
                    ? event
                    : (event.target as HTMLInputElement)?.value ?? '';
                this.$emit('field-change', this.row.key, field, value);
            },
            /**
             * PhoneInput reports a keystroke as two events fired back to back — `change` with the
             * normalized number, then `validityChange`. Both are folded into a single
             * `phone-change` so the parent applies one update; emitting them separately meant the
             * second update overwrote the first, dropping the number the user had just typed.
             */
            onPhoneChange(value: string): void {
                this.latestPhone = value;
                this.emitPhoneChange();
            },
            onPhoneValidityChange(valid: boolean): void {
                this.latestPhoneValid = valid;
                this.emitPhoneChange();
            },
            emitPhoneChange(): void {
                this.$emit(
                    'phone-change',
                    this.row.key,
                    this.latestPhone ?? this.row.phone,
                    this.latestPhoneValid ?? this.row.phoneValid,
                );
            },
            onCoverageInput(options: DropdownOption[]): void {
                this.$emit(
                    'coverages-change',
                    this.row.key,
                    (options || []).map((option) => String(option.value)),
                );
            },
        },
    });
</script>
